import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  getCatalogCoverage,
  listAllCatalogFrameworks,
  listManualChecks,
  unwrapGet,
} from "../../../../helpers/auditHub_helper";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_SUBTLE,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
} from "../theme";
import { formatDateTime } from "../utils";

const PAGE_SIZE = 200;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem", color: AH_MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: AH_TEXT },
  "& .MuiInputBase-input": { fontSize: "0.875rem", color: AH_TEXT },
};

const linkButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  height: 30,
  px: 1.25,
  borderRadius: "8px",
  color: "#1976d2",
  "&:hover": { backgroundColor: "#eff6ff" },
  "&.Mui-disabled": { color: "#94a3b8" },
};

const statTileSx = {
  border: `1px solid ${AH_BORDER}`,
  borderRadius: "10px",
  backgroundColor: AH_WHITE,
  px: 1.5,
  py: 1.25,
  minWidth: 0,
};

const StatTile = ({ label, value, tone }) => (
  <Box sx={{ ...statTileSx, ...(tone === "warn" ? { borderColor: "#fed7aa", backgroundColor: "#fff7ed" } : null) }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: AH_MUTED }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 700, color: tone === "warn" ? "#c2410c" : AH_TEXT, lineHeight: 1.3, mt: 0.25 }}>
      {Number.isFinite(value) ? value : "—"}
    </Typography>
  </Box>
);

const InlineError = ({ message, onRetry }) => (
  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, py: 1 }}>
    <Typography role="alert" sx={{ fontSize: 13, color: "#b91c1c", overflowWrap: "anywhere" }}>
      {message}
    </Typography>
    {onRetry ? (
      <Button variant="text" onClick={onRetry} sx={linkButtonSx}>
        Retry
      </Button>
    ) : null}
  </Box>
);

const groupByControl = (items) => {
  const groups = new Map();
  items.forEach((item) => {
    if (!groups.has(item.controlCode)) {
      groups.set(item.controlCode, { controlCode: item.controlCode, controlTitle: item.controlTitle, checks: [] });
    }
    groups.get(item.controlCode).checks.push(item);
  });
  return [...groups.values()].map((group) => ({
    ...group,
    checks: [...group.checks].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
  }));
};

const FrameworkCatalogDialog = ({ open, onClose }) => {
  const [frameworks, setFrameworks] = useState({ status: "idle", items: [], error: null });
  const [frameworksToken, setFrameworksToken] = useState(0);
  const [framework, setFramework] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [coverage, setCoverage] = useState({ status: "idle", data: null, error: null });
  const [coverageToken, setCoverageToken] = useState(0);
  const [checks, setChecks] = useState({ status: "idle", items: [], total: 0, page: 0, error: null });
  const [checksToken, setChecksToken] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(null);
  const checksKeyRef = useRef("");

  const complianceKey = framework?.complianceKey || "";
  const checksKey = `${complianceKey}|${includeInactive}`;
  checksKeyRef.current = checksKey;

  useEffect(() => {
    if (!open) return;
    setFramework(null);
    setIncludeInactive(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setFrameworks({ status: "loading", items: [], error: null });
    listAllCatalogFrameworks()
      .then((items) => {
        if (cancelled) return;
        const sorted = [...items].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
        setFrameworks({ status: "ready", items: sorted, error: null });
      })
      .catch((err) => {
        if (!cancelled) setFrameworks({ status: "error", items: [], error: err?.message || "Unable to load frameworks." });
      });
    return () => {
      cancelled = true;
    };
  }, [open, frameworksToken]);

  useEffect(() => {
    if (!open || !complianceKey) {
      setCoverage({ status: "idle", data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setCoverage({ status: "loading", data: null, error: null });
    getCatalogCoverage(complianceKey)
      .then((res) => {
        if (!cancelled) setCoverage({ status: "ready", data: unwrapGet(res), error: null });
      })
      .catch((err) => {
        if (!cancelled) setCoverage({ status: "error", data: null, error: err?.message || "Unable to load coverage." });
      });
    return () => {
      cancelled = true;
    };
  }, [open, complianceKey, coverageToken]);

  useEffect(() => {
    if (!open || !complianceKey) {
      setChecks({ status: "idle", items: [], total: 0, page: 0, error: null });
      return undefined;
    }
    let cancelled = false;
    setChecks({ status: "loading", items: [], total: 0, page: 0, error: null });
    setMoreError(null);
    listManualChecks({ complianceKey, includeInactive: includeInactive || undefined, page: 1, size: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        const body = unwrapGet(res);
        const items = Array.isArray(body?.items) ? body.items : [];
        setChecks({ status: "ready", items, total: Number(body?.total) || items.length, page: 1, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setChecks({ status: "error", items: [], total: 0, page: 0, error: err?.message || "Unable to load questions." });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, complianceKey, includeInactive, checksToken]);

  const loadMore = async () => {
    if (loadingMore || !complianceKey) return;
    const key = checksKeyRef.current;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const body = unwrapGet(
        await listManualChecks({
          complianceKey,
          includeInactive: includeInactive || undefined,
          page: checks.page + 1,
          size: PAGE_SIZE,
        })
      );
      if (checksKeyRef.current !== key) return;
      const batch = Array.isArray(body?.items) ? body.items : [];
      setChecks((prev) => {
        const seen = new Set(prev.items.map((item) => item.manualCheckId));
        return {
          ...prev,
          items: [...prev.items, ...batch.filter((item) => !seen.has(item.manualCheckId))],
          total: Number(body?.total) || prev.total,
          page: prev.page + 1,
        };
      });
    } catch (err) {
      if (checksKeyRef.current === key) setMoreError(err?.message || "Unable to load more questions.");
    } finally {
      setLoadingMore(false);
    }
  };

  const groups = useMemo(() => groupByControl(checks.items), [checks.items]);
  const lastSynced = useMemo(
    () =>
      checks.items.reduce((latest, item) => {
        const value = item.lastSyncedAt ? new Date(item.lastSyncedAt).getTime() : NaN;
        return Number.isFinite(value) && (!latest || value > latest) ? value : latest;
      }, 0),
    [checks.items]
  );

  const withoutChecks = coverage.data?.withoutChecks;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="framework-catalog-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 860 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
          flexShrink: 0,
        }}
      >
        <Typography id="framework-catalog-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Framework Catalog
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED, mb: 2, lineHeight: 1.6 }}>
          Review the manual questions loaded for a framework. Assessments created now copy exactly these controls and questions.
        </Typography>

        {frameworks.status === "error" ? (
          <InlineError message={frameworks.error} onRetry={() => setFrameworksToken((value) => value + 1)} />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
            <Autocomplete
              id="framework-catalog-select"
              size="small"
              options={frameworks.items}
              value={framework}
              loading={frameworks.status === "loading"}
              onChange={(_event, option) => setFramework(option || null)}
              getOptionLabel={(option) => option.name || option.complianceKey || `Framework ${option.complianceAddonId}`}
              getOptionDisabled={(option) => !option.complianceKey}
              isOptionEqualToValue={(a, b) => a.complianceAddonId === b.complianceAddonId}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={key} {...rest} sx={{ display: "block !important", py: "6px !important" }}>
                    <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT }}>
                      {option.name || option.complianceKey || `Framework ${option.complianceAddonId}`}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                      {[
                        option.complianceKey,
                        `${option.manualControlCount} manual control${option.manualControlCount === 1 ? "" : "s"}`,
                        option.isScanEnabled ? "" : "Scan disabled",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Framework"
                  placeholder="Select a framework"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {frameworks.status === "loading" ? <CircularProgress size={16} sx={{ color: AH_MUTED }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={fieldSx}
                />
              )}
              sx={{ flex: "1 1 320px", minWidth: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeInactive}
                  disabled={!complianceKey}
                  onChange={(event) => setIncludeInactive(event.target.checked)}
                  sx={{ "&.Mui-checked": { color: AH_ACCENT } }}
                />
              }
              label={<Typography sx={{ fontSize: 13, color: complianceKey ? AH_TEXT : "#94a3b8" }}>Include inactive questions</Typography>}
              sx={{ mr: 0 }}
            />
          </Box>
        )}

        {complianceKey ? (
          <Box sx={{ mt: 2.5 }}>
            {coverage.status === "loading" ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : coverage.status === "error" ? (
              <InlineError message={coverage.error} onRetry={() => setCoverageToken((value) => value + 1)} />
            ) : coverage.data ? (
              <>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                  <StatTile label="Manual controls" value={coverage.data.manualControls} />
                  <StatTile label="Active questions" value={coverage.data.questionCount} />
                  <StatTile label="Controls without questions" value={withoutChecks} tone={withoutChecks > 0 ? "warn" : undefined} />
                </Box>
                {withoutChecks > 0 ? (
                  <Typography sx={{ fontSize: 12, color: "#b45309", mt: 1, lineHeight: 1.5 }}>
                    {withoutChecks} control{withoutChecks === 1 ? " has" : "s have"} every question deactivated and would be left out of a new assessment.
                  </Typography>
                ) : null}
              </>
            ) : null}

            <Box sx={{ borderTop: `1px solid ${AH_BORDER}`, my: 2.5 }} />

            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.25 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: AH_MUTED }}>
                Questions
              </Typography>
              {lastSynced ? (
                <Typography sx={{ fontSize: 12, color: AH_MUTED }}>Last synced {formatDateTime(lastSynced)}</Typography>
              ) : null}
            </Box>

            {checks.status === "loading" ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : checks.status === "error" ? (
              <InlineError message={checks.error} onRetry={() => setChecksToken((value) => value + 1)} />
            ) : !groups.length ? (
              <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No questions are loaded for this framework.</Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {groups.map((group) => (
                    <Box
                      key={group.controlCode}
                      sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "10px", backgroundColor: AH_WHITE, px: 1.5, py: 1.25 }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
                        <Box
                          component="span"
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: AH_SUBTLE,
                            backgroundColor: AH_TRACK,
                            border: `1px solid ${AH_BORDER}`,
                            borderRadius: "6px",
                            px: 0.875,
                            lineHeight: 1.6,
                          }}
                        >
                          {group.controlCode}
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT, overflowWrap: "anywhere" }}>
                          {group.controlTitle}
                        </Typography>
                      </Box>
                      <Box component="ol" sx={{ listStyle: "none", m: 0, mt: 1, p: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {group.checks.map((check) => (
                          <Box component="li" key={check.manualCheckId} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: AH_MUTED, minWidth: 20, lineHeight: 1.6 }}>
                              {check.sequence}.
                            </Typography>
                            <Typography
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 13,
                                lineHeight: 1.6,
                                color: check.isActive ? "#374151" : AH_MUTED,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {check.question}
                              {!check.isActive ? (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.75,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    height: 18,
                                    px: 0.75,
                                    borderRadius: "999px",
                                    fontSize: 10.5,
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    backgroundColor: "#f3f4f6",
                                    border: "1px solid #e5e7eb",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  Inactive
                                </Box>
                              ) : null}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                  <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                    Showing {checks.items.length} of {checks.total} questions
                  </Typography>
                  {checks.items.length < checks.total ? (
                    <Button
                      variant="text"
                      disabled={loadingMore}
                      onClick={loadMore}
                      startIcon={loadingMore ? <CircularProgress size={14} color="inherit" /> : null}
                      sx={linkButtonSx}
                    >
                      {loadingMore ? "Loading..." : "Load more"}
                    </Button>
                  ) : null}
                  {moreError ? (
                    <Typography role="alert" sx={{ fontSize: 12, color: "#b91c1c" }}>
                      {moreError}
                    </Typography>
                  ) : null}
                </Box>
              </>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            minWidth: 140,
            height: 38,
            px: 2,
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: 400,
            fontSize: "0.875rem",
            boxShadow: "none",
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default FrameworkCatalogDialog;
